/**
 * One tutorial card: watch, plus DataTable row-edit / row-archive in manage mode.
 */
import * as React from 'react';
import { Play } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Button } from '@/components/forms/Button';
import type { TutorialVideo } from '@/types/tutorial.types';
import { isTutorialAdminOnly, tutorialModeLabel } from './tutorial-grid-utils';

/** Card actions. Edit and archive stay hidden when the caller cannot manage the catalog. */
interface TutorialCardProps {
  video: TutorialVideo;
  canEdit: boolean;
  canDelete: boolean;
  onWatch: (video: TutorialVideo) => void;
  onEdit: (id: string) => void;
  onDelete: (video: TutorialVideo) => void;
}

/** Catalog card. Manage actions sit on the preview, top-right. */
export const TutorialCard: React.FC<TutorialCardProps> = ({
  video,
  canEdit,
  canDelete,
  onWatch,
  onEdit,
  onDelete,
}) => {
  const handleWatch = () => onWatch(video);
  const handleEdit = () => onEdit(video.id);
  const handleDelete = () => onDelete(video);
  const showActions = canEdit || canDelete;

  function renderAudienceHint(): React.ReactNode {
    if (!canEdit || !isTutorialAdminOnly(video)) return null;
    return <LabelBadge label="Administration uniquement" variant="outlined" size="sm" />;
  }

  function renderManageButtons(): React.ReactNode {
    if (!showActions) return null;
    return (
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        {canEdit ? <Button preset="row-edit" onClick={handleEdit} /> : null}
        {canDelete ? <Button preset="row-archive" onClick={handleDelete} /> : null}
      </div>
    );
  }

  function renderFooter(): React.ReactNode {
    return (
      <Button
        type="button"
        variant="primary"
        className="mt-auto w-full font-semibold uppercase tracking-wide"
        icon={Play}
        onClick={handleWatch}
      >
        Regarder
      </Button>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="relative aspect-video w-full overflow-hidden bg-muted">
        <button
          type="button"
          onClick={handleWatch}
          className="absolute inset-0 flex items-center justify-center bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-2-nuance-1)]"
        >
          <Play className="h-10 w-10 text-[var(--color-2-nuance-1)]" />
        </button>
        {renderManageButtons()}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-semibold leading-snug text-foreground line-clamp-2">
            {video.name}
          </h3>
          {canEdit ? (
            <LabelBadge label={tutorialModeLabel(video.mode)} variant="ghost" size="sm" />
          ) : null}
          {renderAudienceHint()}
        </div>
        {renderFooter()}
      </div>
    </div>
  );
};
